import { cn } from "../../lib/utils";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatDecimal } from "./IpfTotalLine";

const chipClass = (active, disabled = false) =>
	cn(
		"h-8 px-2.5 rounded-sm border text-xs font-heading whitespace-nowrap transition-colors",
		active
			? "border-volt/60 bg-volt/15 text-volt"
			: "border-border bg-background/40 text-muted-foreground hover:text-foreground",
		disabled && "opacity-50 cursor-not-allowed hover:text-muted-foreground",
	);

export const coefficientName = (kind, t) =>
	kind === "ipf_gl" ? t("coefIpfGl") : t("coefSinclair");

/**
 * I filtri della classifica che dipendono dal peso (D14 di docs/sport-config).
 *
 * Compaiono solo se il modello di chi guarda li usa — il server manda
 * `bodyweight` solo allora — cosi' nel CrossFit la classifica resta identica:
 *
 *  - «Tutte le categorie» / «La mia categoria», nei modelli per categoria;
 *  - «Ordina per kg» / «Sinclair» (o «IPF GL», solo sulla panca), nei modelli
 *    con un coefficiente.
 *
 * Gli stessi chip del resto della classifica, che vanno a capo sul telefono.
 *
 * Props: info (`data.bodyweight`), classFilter, onClassFilterChange(key|""),
 * sort ("value" | "coefficient"), onSortChange.
 */
export function LeaderboardBodyweightFilters({
	info,
	classFilter = "",
	onClassFilterChange,
	sort = "value",
	onSortChange,
}) {
	const { t } = useLanguage();
	if (!info || (!info.by_class && !info.coefficient)) return null;
	const myClass = info.my_weight_class || null;

	return (
		<div className="space-y-2" data-testid="leaderboard-bodyweight">
			{info.by_class && (
				<div className="flex flex-wrap items-center gap-1">
					<button
						type="button"
						aria-pressed={!classFilter}
						className={chipClass(!classFilter)}
						onClick={() => onClassFilterChange?.("")}
					>
						{t("lbAllClasses")}
					</button>
					<button
						type="button"
						aria-pressed={!!classFilter && classFilter === myClass?.key}
						disabled={!myClass}
						title={myClass ? undefined : t("lbAddWeightHint")}
						className={chipClass(!!classFilter && classFilter === myClass?.key, !myClass)}
						onClick={() => myClass && onClassFilterChange?.(myClass.key)}
					>
						{myClass
							? t("lbMyClass").replace("{label}", myClass.label)
							: t("lbMyClassUnknown")}
					</button>
				</div>
			)}
			{info.by_class && !myClass && (
				<p className="text-[11px] text-muted-foreground">{t("lbAddWeightHint")}</p>
			)}
			{info.coefficient && (
				<div className="flex flex-wrap items-center gap-1">
					<span className="text-[11px] text-muted-foreground mr-0.5">{t("lbSortBy")}</span>
					<button
						type="button"
						aria-pressed={sort !== "coefficient"}
						className={chipClass(sort !== "coefficient")}
						onClick={() => onSortChange?.("value")}
					>
						{t("lbSortValue")}
					</button>
					<button
						type="button"
						aria-pressed={sort === "coefficient"}
						className={chipClass(sort === "coefficient")}
						onClick={() => onSortChange?.("coefficient")}
					>
						{coefficientName(info.coefficient, t)}
					</button>
				</div>
			)}
		</div>
	);
}

/**
 * La metrica di una riga quando la classifica e' ordinata per coefficiente:
 * i punti in grande, i chili sotto. `null` negli altri casi, e il chiamante
 * usa la sua resa di sempre.
 */
export function coefficientMetric(entry, sort, t, language) {
	if (sort !== "coefficient") return null;
	const points = entry?.coefficient?.value;
	const kilos = entry?.metric_label || entry?.value;
	return (
		<span>
			{points !== null && points !== undefined
				? formatDecimal(points, language)
				: "—"}
			<span className="block text-[11px] font-normal text-muted-foreground">
				{kilos}
			</span>
		</span>
	);
}
